import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Database, 
  Play, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Package, 
  FileText, 
  DollarSign, 
  BarChart3,
  Clock,
  Zap,
  Trash2,
  Download,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { generateTestData, getTestDataStatus, deleteAllData, resetAdminFunctions, resetAllDataWithAdmin } from '../../config/api';
import { SAMPLE_CSVS, downloadCsv } from '../../data/sampleCsvs';

const TestDataManagement = () => {
  const { user } = useAuth();
  const { refreshPermissions, getPermissionSummary } = usePermissions();
  const queryClient = useQueryClient();
  
  // State
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResettingAdmin, setIsResettingAdmin] = useState(false);
  const [isResettingAll, setIsResettingAll] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [lastGeneration, setLastGeneration] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false);

  // Fetch current data status
  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['test-data-status'],
    queryFn: getTestDataStatus
  });

  // Generate test data mutation
  const generateMutation = useMutation({
    mutationFn: generateTestData,
    onSuccess: (data) => {
      toast.success('Test data generated successfully!');
      setLastGeneration({
        timestamp: new Date(),
        data: data.data
      });
      queryClient.invalidateQueries({ queryKey: ['test-data-status'] });
      setIsGenerating(false);
      setGenerationProgress(0);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to generate test data');
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  });

  // Delete all data mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAllData,
    onSuccess: (data) => {
      toast.success('All data deleted successfully! (Users and roles preserved)');
      queryClient.invalidateQueries({ queryKey: ['test-data-status'] });
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete data');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  });

  // Reset admin functions mutation
  const resetAdminMutation = useMutation({
    mutationFn: resetAdminFunctions,
    onSuccess: (data) => {
      toast.success('Admin functions reset successfully!');
      queryClient.invalidateQueries({ queryKey: ['test-data-status'] });
      setIsResettingAdmin(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reset admin functions');
      setIsResettingAdmin(false);
    }
  });

  // Reset all data with admin mutation
  const resetAllMutation = useMutation({
    mutationFn: resetAllDataWithAdmin,
    onSuccess: (data) => {
      toast.success('All data reset and admin functions restored successfully!');
      queryClient.invalidateQueries({ queryKey: ['test-data-status'] });
      setIsResettingAll(false);
      setShowResetAllConfirm(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reset all data');
      setIsResettingAll(false);
      setShowResetAllConfirm(false);
    }
  });

  // Handlers
  const handleGenerateTestData = async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    setGenerationProgress(0);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 10;
      });
    }, 500);
    
    try {
      await generateMutation.mutateAsync();
    } catch (error) {
      clearInterval(progressInterval);
    }
  };

  const handleDeleteAllData = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync();
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleResetAdminFunctions = async () => {
    if (isResettingAdmin) return;
    
    setIsResettingAdmin(true);
    try {
      await resetAdminMutation.mutateAsync();
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleResetAllData = async () => {
    if (isResettingAll) return;
    
    setIsResettingAll(true);
    try {
      await resetAllMutation.mutateAsync();
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const getStatusIcon = (count) => {
    if (count === 0) return <AlertTriangle className="h-5 w-5 text-red-500" />;
    if (count < 10) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  };

  const getStatusColor = (count) => {
    if (count === 0) return 'bg-red-100 text-red-800';
    if (count < 10) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (statusLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Test Data Management</h1>
          <p className="text-gray-600">Generate and manage test data for development and testing</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => refetchStatus()}
            title="Refresh Status"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Status
          </button>
          <button
            onClick={handleResetAdminFunctions}
            title="Reset Admin Functions"
            disabled={isResettingAdmin}
            className={`flex items-center px-4 py-2 text-sm font-medium text-white rounded-md ${
              isResettingAdmin 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isResettingAdmin ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Reset Admin Functions
              </>
            )}
          </button>
          <button
            onClick={() => setShowResetAllConfirm(true)}
            title="Reset All Data"
            disabled={isResettingAll}
            className={`flex items-center px-4 py-2 text-sm font-medium text-white rounded-md ${
              isResettingAll 
                ? 'bg-orange-400 cursor-not-allowed' 
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {isResettingAll ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Resetting All...
              </>
            ) : (
              <>
                <Database className="h-4 w-4 mr-2" />
                Reset All Data
              </>
            )}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            title="Delete All Data"
            disabled={isDeleting}
            className={`flex items-center px-4 py-2 text-sm font-medium text-white rounded-md ${
              isDeleting 
                ? 'bg-red-400 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isDeleting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All Data
              </>
            )}
          </button>
          <button
            onClick={() => {
              refreshPermissions();
              toast.success('Permissions refreshed!');
            }}
            title="Refresh Permissions"
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Permissions
          </button>
        </div>
      </div>

      {/* Permission Status */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Permission Status</h2>
          <button
            onClick={() => {
              refreshPermissions();
              toast.success('Permissions refreshed!');
            }}
            title="Refresh"
            className="flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {(() => {
            const summary = getPermissionSummary();
            return (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
                  <div className="text-sm text-gray-600">Total Permissions</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{summary.granted}</div>
                  <div className="text-sm text-gray-600">Granted</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{summary.revoked}</div>
                  <div className="text-sm text-gray-600">Revoked</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{summary.categories.length}</div>
                  <div className="text-sm text-gray-600">Categories</div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Sample Data Files */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-2 mb-1">
          <Download className="h-5 w-5 text-indigo-600" />
          <h2 className="text-lg font-medium text-gray-900">Sample Data Files</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Download a sample CSV for each feed. Customers and Sales Data can be re-imported through their upload screens; the others are format references.
        </p>
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {SAMPLE_CSVS.map((s) => {
            const meta = {
              customers: { Icon: Users, color: 'text-blue-600', bg: 'bg-blue-100' },
              products: { Icon: Package, color: 'text-emerald-600', bg: 'bg-emerald-100' },
              contracts: { Icon: FileText, color: 'text-amber-600', bg: 'bg-amber-100' },
              sales: { Icon: Database, color: 'text-cyan-600', bg: 'bg-cyan-100' },
              chargebacks: { Icon: BarChart3, color: 'text-rose-600', bg: 'bg-rose-100' },
            }[s.key];
            const Icon = meta.Icon;
            return (
              <div key={s.key} className="flex items-center justify-between px-4 py-3 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg ${meta.bg}`}>
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{s.label}</p>
                    <p className="text-xs text-gray-500">{s.note}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.uploadable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {s.uploadable ? 'Re-importable' : 'Template only'}
                  </span>
                  <button
                    onClick={() => downloadCsv(s)}
                    title={`Download ${s.filename}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Data Status */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-gray-900">Current Data Status</h2>
          <span className="text-sm text-gray-500">
            Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Customers</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900">
                  {statusData?.data?.customers || 0}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData?.data?.customers || 0)}`}>
                  {getStatusIcon(statusData?.data?.customers || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Package className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Products</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900">
                  {statusData?.data?.products || 0}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData?.data?.products || 0)}`}>
                  {getStatusIcon(statusData?.data?.products || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Contracts</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900">
                  {statusData?.data?.contracts || 0}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData?.data?.contracts || 0)}`}>
                  {getStatusIcon(statusData?.data?.contracts || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Sales Records</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900">
                  {statusData?.data?.salesRecords || 0}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData?.data?.salesRecords || 0)}`}>
                  {getStatusIcon(statusData?.data?.salesRecords || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Chargebacks</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900">
                  {statusData?.data?.chargebacks || 0}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData?.data?.chargebacks || 0)}`}>
                  {getStatusIcon(statusData?.data?.chargebacks || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Database className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Batches</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900">
                  {statusData?.data?.batches || 0}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData?.data?.batches || 0)}`}>
                  {getStatusIcon(statusData?.data?.batches || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Test Data Generation */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Generate Test Data</h2>
            <p className="text-sm text-gray-600">
              Generate comprehensive test data including customers, products, contracts, sales records, and chargebacks
            </p>
          </div>
          <button
            onClick={handleGenerateTestData}
            title="Generate Test Data"
            disabled={isGenerating}
            className={`flex items-center px-6 py-3 text-sm font-medium text-white rounded-md ${
              isGenerating 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {isGenerating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Generate Test Data
              </>
            )}
          </button>
        </div>

        {/* Generation Progress */}
        {isGenerating && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Generation Progress</span>
              <span className="text-sm text-gray-500">{Math.round(generationProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${generationProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Generating 50 customers, 5 contracts, 100 products, 500 sales records, and 10 chargebacks...
            </p>
          </div>
        )}

        {/* Generation Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">What will be generated:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>50 realistic pharmaceutical customers</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>100 pharmaceutical products with NDCs</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>5 contracts with pricing terms</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>500 sales records with realistic data</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>10 chargebacks for processing</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Data includes:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>Industry-standard NDC codes</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>Realistic pricing and contracts</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>EDI transaction data</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>Validation scenarios</span>
              </li>
              <li className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>Compliance and audit trails</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Last Generation Results */}
      {lastGeneration && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Last Generation Results</h2>
            <span className="text-sm text-gray-500">
              {lastGeneration.timestamp.toLocaleString()}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{lastGeneration.data.customers}</p>
              <p className="text-sm text-gray-600">Customers</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{lastGeneration.data.products}</p>
              <p className="text-sm text-gray-600">Products</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{lastGeneration.data.contracts}</p>
              <p className="text-sm text-gray-600">Contracts</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{lastGeneration.data.salesRecords}</p>
              <p className="text-sm text-gray-600">Sales Records</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{lastGeneration.data.chargebacks}</p>
              <p className="text-sm text-gray-600">Chargebacks</p>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-green-800">
                Total Chargeback Amount: ${lastGeneration.data.totalChargebackAmount?.toLocaleString() || '0'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => window.open('/app/chargebacks/processing', '_blank')}
            title="View Chargeback Processing"
            className="flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            View Chargeback Processing
          </button>
          <button
            onClick={() => window.open('/app/batches', '_blank')}
            title="View Batches"
            className="flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
          >
            <Database className="h-4 w-4 mr-2" />
            View Batches
          </button>
          <button
            onClick={() => window.open('/app/sales-data', '_blank')}
            title="View Sales Data"
            className="flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            View Sales Data
          </button>
        </div>
      </div>

      {/* Reset All Data Confirmation Dialog */}
      {showResetAllConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Reset All Data</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  This will completely reset the database and recreate all admin functions. This action cannot be undone.
                </p>
                <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-800 font-medium">
                    ⚠️ This will:
                  </p>
                  <ul className="text-sm text-orange-700 mt-2 space-y-1">
                    <li>• Delete ALL existing data</li>
                    <li>• Recreate system roles and admin user</li>
                    <li>• Seed all permissions</li>
                    <li>• Generate fresh test data</li>
                    <li>• Restore company settings</li>
                  </ul>
                  <p className="text-sm text-green-800 font-medium mt-2">
                    ✅ Admin functions will be fully restored
                  </p>
                </div>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setShowResetAllConfirm(false)}
                  title="Cancel"
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetAllData}
                  title="Reset All Data"
                  disabled={isResettingAll}
                  className={`px-4 py-2 text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                    isResettingAll
                      ? 'bg-orange-400 text-white cursor-not-allowed'
                      : 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500'
                  }`}
                >
                  {isResettingAll ? 'Resetting...' : 'Reset All Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete All Data</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete ALL data? This action cannot be undone.
                </p>
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ This will delete:
                  </p>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                    <li>• All customers</li>
                    <li>• All products</li>
                    <li>• All contracts</li>
                    <li>• All chargebacks</li>
                    <li>• All sales data</li>
                    <li>• All batches</li>
                  </ul>
                  <p className="text-sm text-green-800 font-medium mt-2">
                    ✅ Users and roles will be preserved
                  </p>
                </div>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  title="Cancel"
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllData}
                  title="Delete All Data"
                  disabled={isDeleting}
                  className={`px-4 py-2 text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                    isDeleting
                      ? 'bg-red-400 text-white cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {isDeleting ? 'Deleting...' : 'Delete All Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestDataManagement;
